import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useLeaderboardNavSync } from './hooks/use-leaderboard-nav-sync';
import { leaderboardNavSelector } from 'store/modules/brownlow-tracker/leaderboard/selectors';
import { ClubNavItem, SeasonNavItem, Search } from './components/nav-items';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { ShowPredicted } from '../nav-items/ShowPredicted';
import { LeaderboardTableFetcher } from './components/table/LeaderboardTableFetcher';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { UPDATE_NAV_ITEM } from 'store/modules/brownlow-tracker/leaderboard/actions';
import { getTranslation } from 'common/react/utils/translations';

const NavWrapper = ({ children }) => (
    <div className="competition-nav filter-dropdown">{children}</div>
);

NavWrapper.propTypes = {
    children: PropTypes.node.isRequired
};

export const Leaderboard = ({
    seasonsToShow,
    seasonPid,
    competitionId,
    shouldHidePredicted,
    title
}) => {
    const nav = useSelector(leaderboardNavSelector);
    const dispatch = useDispatch();
    const leaderboardNavRef = useRef();

    const options = useCompetitionNavigationOptions(nav);
    const windowSize = useWindowSizeV2();

    options.defaultSeason = seasonPid ?? seasonPid;
    options.seasonsToShow = seasonsToShow ?? seasonsToShow;

    const newOpts = {
        ...options,
        competitionId: competitionId
    };
    useLeaderboardNavSync(newOpts);

    const classPrefix = 'brownlow-leaderboard';

    const shouldHidePredictedBool =
        shouldHidePredicted === 'true' ? true : false;

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    const selectedSeasonPid = useMemo(() => {
        return options?.seasons?.find((season) => season.id === nav.season?.id)
            ?.providerId;
    }, [nav, options]);

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: ['season', 'club', 'showPredicted', 'sortByFavourites'],
        filtersRef: leaderboardNavRef
    });

    const getInitialSeasonId = () => {
        return options.defaultSeason
            ? options.seasons?.find(
                  (season) => season.providerId === options.defaultSeason
              )?.id
            : options.seasons?.[0]?.id;
    };

    const getInitialTeamId = () => {
        return options.teams?.[0]?.id || -1;
    };

    const hasChangedFromDefaults = () => {
        if (!nav?.season || !nav?.team) {
            return false;
        }

        const initialSeasonId = getInitialSeasonId();
        const initialTeamId = getInitialTeamId();

        const isSeasonChanged = nav?.season?.id !== initialSeasonId;
        const isTeamChanged = nav?.team?.id !== initialTeamId;

        return isSeasonChanged || isTeamChanged;
    };

    const resetToDefaults = () => {
        const initialSeasonId = getInitialSeasonId();
        const initialTeamId = getInitialTeamId();

        if (nav?.season?.id !== initialSeasonId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: { id: initialSeasonId }
                })
            );
        }
        if (nav?.team?.id !== initialTeamId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'team',
                    value: { id: initialTeamId }
                })
            );
        }
    };

    return (
        <div className={classPrefix} ref={leaderboardNavRef}>
            <StatsSection.Main NavWrapper={NavWrapper}>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    <StatsSection.NavItems>
                        <div className="player-awards-wrapper">
                            <SeasonNavItem
                                classes="season-nav-item player-awards-season-nav-item"
                                seasonsToShow={seasonsToShow}
                            />
                            <ClubNavItem title={title} />
                        </div>
                        {showResponsiveItems ? (
                            <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                                <StatsSection.InfoButton
                                    shouldHidePredicted={
                                        shouldHidePredictedBool
                                    }
                                    seasonPid={selectedSeasonPid}
                                    tooltipContent="PlayerAwardsLeaderboardTooltip"
                                    className="p-none"
                                />
                                <SettingsButton />
                            </StatsSection.SettingsButton>
                        ) : null}
                        {hasChangedFromDefaults() && !showResponsiveItems ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </StatsSection.NavItems>
                    {hasChangedFromDefaults() && showResponsiveItems ? (
                        <StatsSection.NavFilterItems>
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        </StatsSection.NavFilterItems>
                    ) : null}

                    <StatsSection.SettingsButton>
                        {!showResponsiveItems && !shouldHidePredictedBool ? (
                            <div
                                className={`${classPrefix}-nav__extras`}
                                style={{
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                <ShowPredicted />
                            </div>
                        ) : null}
                        {!showResponsiveItems ? <Search /> : null}
                        <StatsSection.InfoButton
                            shouldHidePredicted={shouldHidePredictedBool}
                            seasonPid={selectedSeasonPid}
                            tooltipContent="PlayerAwardsLeaderboardTooltip"
                            className="p-none"
                        />
                        <SettingsButton />
                    </StatsSection.SettingsButton>

                    {showResponsiveItems ? (
                        <StatsSection.SearchBarMobile>
                            <div className={`${classPrefix}-nav__extras`}>
                                <Search />
                            </div>
                            {!shouldHidePredictedBool ? (
                                <ShowPredicted />
                            ) : null}
                        </StatsSection.SearchBarMobile>
                    ) : null}
                </StatsSection.Nav>
                <StatsSection.Content>
                    <StatsSection.ContentBody>
                        <LeaderboardTableFetcher
                            cssClass={classPrefix}
                            shouldHidePredicted={shouldHidePredictedBool}
                            title={title}
                            sortByFavourites={nav.sortByFavourites}
                        />
                    </StatsSection.ContentBody>
                </StatsSection.Content>
            </StatsSection.Main>
        </div>
    );
};

Leaderboard.propTypes = {
    seasonsToShow: PropTypes.string,
    seasonPid: PropTypes.string,
    competitionId: PropTypes.string,
    shouldHidePredicted: PropTypes.string,
    title: PropTypes.string
};
